import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PanelRight, PanelRightDashed } from "lucide-react";

import { KoryPointingLayer } from "@/components/kory/kory-pointing-layer";
import { AttachedValueChip, attachedValueRail } from "@/components/kory/attached-value-chip";
import { DriveFocusChip, DriveHeadline } from "@/components/kory/drive-focus";
import { TrayComposer, TrayComposerStatus } from "@/components/kory/global-kory-tray-composer";
import { SuggestionSearchBand, useSearchBandRows } from "@/components/kory/global-kory-tray-search-band";
import { useWorkingZone } from "@/components/kory/global-kory-tray-zone";
import { CaptionFold } from "@/components/overview/cos-morning/cos-live-turn";
import { KoryIcon } from "@/components/kory/kory-icon";
import { MeetingModeProvider, ThisMeetingSection, useMeetingMode } from "@/components/kory/meeting";
import { TrayContextRow } from "@/components/kory/tray-context-row";
import { focusableSearchResult, type TraySearch, traySearchLimit, useTraySearch } from "@/components/kory/tray-search-results";
import { KoryTraySettings } from "@/components/kory/tray-settings";
import { useTrayTapDebug } from "@/components/kory/tray-tap-debug";
import { LiveVoiceProvider, useLiveVoiceClaim, useTrayLiveVoice } from "@/components/overview/cos-morning/cos-live-voice";
import { useKoryLiveState } from "@/components/overview/cos-morning/kory-live-state";
import { ConversationActions, LastTurnLines } from "@/components/overview/cos-v2/last-turn";
import {
  KoryRailTalk,
  KoryTalkUnit,
  KoryTrayHalo,
  koryTrayBar,
  useComposerTurn,
  useTrayAsks,
  type TrayAsks,
} from "@/components/overview/cos-v2/talk-unit";
import { lastTurn, replyAfter, useTypedTurn } from "@/components/overview/cos-v2/typed-turn";
import { sessionConversationId, useCosSession, useCosSessionThread } from "@/components/overview/cos-v2/use-cos-session";
import { rolePrompts, starterPrompts } from "@/components/overview/kory-prompts";
import { useRankedFeedGate } from "@/components/feed/use-ranked-feed-gate";
import { PreviewBack, PreviewTrayCue } from "@/components/feed/preview-pill";
import { previewingLine, useKoryPreview } from "@/lib/kory-preview";
import { FocusTray, useTrayItems } from "@/components/todos/tray/focus-tray";
import { trayWork } from "@/components/todos/tray/focus-model";
import { PointingStrip } from "@/components/kory/pointing-strip";
import { asTurnMessages } from "@/components/drives/page/drive-thread-model";
import { useDriveConversation } from "@/components/drives/page/use-drive-conversation";
import { PageLens } from "@/components/todos/tray/page-lens";
import { aboutSubject, TodoTray, type TraySubject } from "@/components/todos/tray/todo-tray";
import { PHONE_TRAY_TOP, TRAY_TOP, trayBucket } from "@/components/todos/tray/tray-model";
import { useActedHereCount } from "@/components/todos/tray/approve-moment";
import {
  unmarkReferencedTodos,
  useLoadNamed,
  useNamedCard,
  useNamedReveal,
  usePointedTargets,
  useReferencedTodos,
  useTrayCall,
} from "@/components/todos/tray/use-referenced-todos";
import { Button } from "@/components/ui/button";
import { DockedTray, DockedTraySideAllowed, type TrayDetent, useSideTray } from "@/components/ui/docked-tray";
import { OverlayScope } from "@/components/ui/overlay-scope";
import { Skeleton } from "@/components/ui/skeleton";
import { useKory } from "@/contexts/kory-context";
import { useReloadHold } from "@/lib/reload-holds";
import { ImpersonationContext } from "@/contexts/impersonation-context";
import { useKoryTray, type DriveFocus, type KoryTrayMode, type PaletteAsk } from "@/contexts/kory-tray-context";
import { askAboutContextLine, paletteEvent, type AskAboutEntity } from "@/lib/kory-palette";
import { useTodoDrawer } from "@/contexts/todo-drawer-context";
import { usePanelStack } from "@/components/ui/panel-stack-context";
import { type TodoDTO, useAgentTodos } from "@/hooks/use-agent-todos";
import { useGlobalTrayFlag } from "@/hooks/use-global-tray-flag";
import { useDrivePage } from "@/hooks/use-drives";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { useProfileData } from "@/hooks/use-profile-data";
import { CELL_PROMPTS, serializeCellContextBlock } from "@/lib/kory-cell-context";
import { toFeedItem, useKoryFeed } from "@/lib/kory-feed";
import { capturePage, capturePageContext } from "@/lib/kory-page-context";
import { pageSharing } from "@/lib/kory-page-sharing";
import { pageHeading } from "@/lib/kory-screen-digest";
import { trayClose, trayEvent, type TrayOpenSource, type TrayScene, type TraySendSource } from "@/lib/kory-tray-events";
import { trayIconVariant, useTrayTheme } from "@/lib/kory-tray-theme";
import {
  EXPLAIN_PROMPTS,
  isTrayShortcut,
  pageShowsAllTodos,
  subjectContextLine,
  trayExcludes,
  type KoryPageSubject,
} from "@/lib/kory-page-subject";
import { cn } from "@/lib/utils";
import { useTypedWaitCue } from "@/lib/voice-live/wait-cues";
import { keepTypingFocus } from "@/lib/keep-composer-focus";

/**
 * The global Kory tray: one docked tray on every page, minimized to the Kory
 * mark and a quiet count. Behind the per-user ``global_tray`` flag; the page's
 * subject (``useKoryPageSubject``) picks what it holds. It rises only when
 * someone asks (the handle, ``K``, a right-click cell capture, the meeting
 * rail) and never on navigation. With the ``cos_session_v2`` flag it talks
 * into the person's one Kory session and shows the replies here, on the page;
 * without it, a message opens Kory chat.
 */
export function GlobalKoryTray({ hostRef }: { hostRef: RefObject<HTMLElement | null> }) {
  const tray = useKoryTray();
  // The route as of this render: an open tray that a new page hides reads it as it unmounts.
  const { pathname } = useLocation();
  const routePath = useRef(pathname);
  routePath.current = pathname;
  const enabled = useGlobalTrayFlag().on;
  const setEnabled = tray?.setEnabled;
  useEffect(() => {
    setEnabled?.(enabled);
  }, [enabled, setEnabled]);
  const readOnly = useContext(ImpersonationContext)?.isReadOnly ?? false;

  if (!tray?.active) return null;
  // A read-only preview runs as someone else, so the chat body never mounts.
  if (readOnly) {
    return <p className="fixed bottom-4 right-4 z-40 text-xs text-muted-foreground print:hidden">Chat is off in preview</p>;
  }
  return (
    <TrayBody
      hostRef={hostRef}
      routePath={routePath}
      subject={tray.subject}
      openNonce={tray.openNonce}
      openTray={tray.openTray}
      raiseNonce={tray.raiseNonce}
      needsWidth={tray.needsWidth}
      brief={tray.subject.mode === "host" && tray.briefClaimed ? tray.setBriefSlot : null}
      askAbout={tray.askAbout}
      setAskAbout={tray.setAskAbout}
      paletteAsks={tray.paletteAsks}
      drive={tray.driveFocus}
      pinDrive={tray.pinDrive}
      unpinDrive={tray.unpinDrive}
      setTrayMode={tray.setTrayMode}
    />
  );
}

/**
 * The tab's one live voice session, under both trays. A question Kory relays from it
 * carries the page it was asked from, as a typed turn in either tray does: the global
 * tray's page with its subject, or Overview, which hosts its own tray.
 */
export function GlobalLiveVoiceProvider({ children }: { children: ReactNode }) {
  const tray = useKoryTray();
  const trayRef = useRef(tray);
  trayRef.current = tray;
  const page = () => {
    const current = trayRef.current;
    if (!current || (!current.active && current.subject.mode !== "host")) return null;
    const captured = capturePage(current.active ? subjectContextLine(current.subject) : null);
    if (!captured) return null;
    const shared = pageSharing(window.location.pathname) !== "none";
    return { path: captured.path, title: captured.title, text: captured.text, shared };
  };
  return (
    <LiveVoiceProvider page={page}>
      <MeetingModeProvider>{children}</MeetingModeProvider>
    </LiveVoiceProvider>
  );
}

/** What Kory points at, ringed in gold on whatever page is showing (both trays' replies): its records and
 *  page parts by ref, and anything else by role and name. */
export function KoryPointedTargets() {
  usePointedTargets();
  return <KoryPointingLayer />;
}

/** The longest direction a Drive takes (``DriveDirectiveRequest``). */
const DRIVE_DIRECTIVE_MAX = 4000;

/** The tray's width, floating as the bottom-right panel or docked beside the page. */
const keepTrayTyping = keepTypingFocus(".kory-tray");
const TRAY_WIDTH = 420;
/** The right panel's desktop width in rem (`sm:max-w-xl` in `PanelStackProvider`). */
const PANEL_REM = 36;

interface TrayRows {
  todos: TodoDTO[];
  ready: boolean;
  /** The rows could not load; never shown as an empty queue. */
  failed: boolean;
  /** Finished rows, loaded once a yes lands here or Kory names a card the open rows lack: a card
   *  that just finished shows its confirmation, and one Kory points at in Done is there to find. */
  done: TodoDTO[];
}

/** A Drive: the person's queue, filtered to the cards that Drive proposed. Otherwise the person's open
 *  queue, less what the page lists itself (``trayExcludes``: a DCC deal page's own next steps). One body
 *  serves both, so a subject loading in under an open tray keeps its draft. */
function useTrayRows(subject: TraySubject | null, excludes: (todo: TodoDTO) => boolean): TrayRows {
  const driveSubject = subject?.type === "drive" ? subject : undefined;
  const shown = (todos: TodoDTO[] | undefined) =>
    (todos ?? []).filter((t) => aboutSubject(t, driveSubject) && !excludes(t));
  const active = useAgentTodos("active");
  const suggested = useAgentTodos("suggested");
  // Update cards are news, not work: an optional extra that never holds the queue up.
  const updates = useAgentTodos("updates");
  const { ids: named, changed } = useReferencedTodos();
  const acted = useActedHereCount();
  const open = shown([...(suggested.data ?? []), ...(active.data ?? [])]);
  const namedMissing = named.some((id) => !open.some((t) => t.id === id));
  // Once a yes lands here it stays on, so the reloads after each yes refresh it with the open rows.
  const completed = useAgentTodos("completed", { enabled: acted > 0 || changed > 0 || namedMissing });
  const ready = active.data !== undefined && suggested.data !== undefined;
  return {
    todos: [...open, ...shown(updates.data)],
    ready,
    failed: !ready && (active.isError || suggested.isError),
    done: shown(completed.data),
  };
}

/** The host column's width, kept current. */
function useHostWidth(hostRef: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hostRef]);
  return width;
}

/**
 * Where the tray sits: it floats as the bottom-right panel over a full-width page, unless the person
 * docked it and the page leaves room beside it (the host column minus the tray is at least what the
 * page needs). A page that needs the width gets it; the tray docks again on the next page with room.
 */
export function trayDocks({ docked, column, needsWidth }: { docked: boolean; column: number; needsWidth: number }): boolean {
  return docked && column - TRAY_WIDTH >= needsWidth;
}

function TrayBody({
  hostRef,
  routePath,
  subject,
  openNonce,
  openTray,
  raiseNonce,
  needsWidth,
  brief,
  askAbout,
  setAskAbout,
  paletteAsks,
  drive,
  pinDrive,
  unpinDrive,
  setTrayMode,
}: {
  hostRef: RefObject<HTMLElement | null>;
  routePath: RefObject<string>;
  subject: KoryPageSubject;
  openNonce: number;
  openTray: () => void;
  raiseNonce: number;
  needsWidth: number;
  /** A host page (Overview) hands its rows over: the tray gives it this element in their place. */
  brief: ((el: HTMLElement | null) => void) | null;
  /** The result last picked in ⌘K: it rides along with a send until you leave its page. */
  askAbout: AskAboutEntity | null;
  setAskAbout: (entity: AskAboutEntity | null) => void;
  /** ⌘K handoffs, in order: each sent here, in this conversation. */
  paletteAsks: PaletteAsk[];
  /** The Drive in focus (its page, or a pin): what you type goes to it as a direction. */
  drive: DriveFocus | null;
  pinDrive: (drive: { id: string; label: string }) => void;
  unpinDrive: () => void;
  setTrayMode: (mode: KoryTrayMode) => void;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const drawer = useTodoDrawer();
  // Any step of the right panel (a deal, a to-do) is the drawer the tray makes room for.
  const panels = usePanelStack();
  const { sendMessage, attachedCells, removeCell, clearCells, launcherOpenNonce, launcherHandledNonce } = useKory();
  // The tray never sends the day's kickoff: that is the Overview card's to send.
  const session = useCosSession();
  const sessionOn = Boolean(session.data?.enabled);
  const thread = useCosSessionThread(session.data, { kickoff: false });
  // A typed turn reads like a Talk turn, with the tray's one halo: checking, replying, then done.
  const live = useKoryLiveState({
    listening: false,
    speaking: false,
    thinking: thread.sending,
    composing: false,
    actionVerb: null,
    todoRun: null,
  });
  const typed = useTypedTurn(thread.messages, thread, () => live.pulseDone("Kory replied"));
  const mobile = useIsMobile();
  useTrayTapDebug();
  // Talk, as on Overview: with the session on and live voice allowed, the tray is the shared talk unit.
  const voice = useTrayLiveVoice();
  const meeting = useMeetingMode();
  const talkOn = sessionOn && voice.available;
  useLiveVoiceClaim(voice, talkOn);
  const talking = talkOn && voice.on;
  useTypedWaitCue(typed.view?.halo === "thinking" && !talking);
  const [composerOpened, setComposerOpened] = useState(false);
  const composerTurn = useComposerTurn({
    sessionOn,
    messages: thread.messages,
    typed: typed.view,
    replying: thread.sending || Boolean(session.data?.replying),
    voice: voice.turn,
    opened: composerOpened,
  });
  // The same starters and next asks as Overview's tray, the starters from your role's prompt set.
  const { data: profile } = useProfileData();
  const trayAsks = useTrayAsks({
    session:
      sessionOn && session.data
        ? { conversationId: sessionConversationId(session.data), day: session.data.brief_date }
        : null,
    messages: thread.messages,
    typed: typed.view,
    voice: voice.turn,
    starters: starterPrompts(rolePrompts(profile?.role)),
  });
  // Last turn only: what you typed here, else the session's latest exchange from anywhere. The rest
  // of today is a tap away on /kory, where the session is pinned; no transcript scrolls in the tray.
  const replying = thread.sending || Boolean(session.data?.replying);
  // A Drive in focus: its own conversation's last turn. It answers a direction on its next wake.
  const drivePage = useDrivePage(drive?.id ?? "", { enabled: Boolean(drive) });
  const driveTalk = useDriveConversation(drive?.id ?? "", drive ? (drivePage.data?.conversation_id ?? null) : null);
  // A paused or ended Drive refuses directions; until its page loads, a send keeps the words.
  const driveWaking = drivePage.data?.state === "active";
  const driveLast = drive ? lastTurn(asTurnMessages(driveTalk.history.data ?? [])) : null;
  // A direction with no answer yet is queued for a wake, not a failed send, and not a reply under way.
  const driveTurn =
    driveLast && driveLast.turn.kory.tone === "hint"
      ? { ...driveLast, turn: { ...driveLast.turn, kory: { ...driveLast.turn.kory, text: "The Drive answers on its next wake." } } }
      : driveLast;
  // A paused or ended Drive takes nothing; a draft over the Drive's limit can't be sent as is.
  const driveClosed = Boolean(drive && drivePage.data && !driveWaking);
  // A ⌘K question asked with a Drive in focus still goes to Kory, so Kory's turn shows until you talk to the Drive again.
  const showDrive = Boolean(drive) && !typed.view;
  // A call's spoken answer is Kory's too: the talk unit can bring it back over the Drive's turn until
  // you talk to the Drive again or the focus moves.
  const voiceLine = voice.turn.kory.tone !== "hint" ? voice.turn.kory.text : "";
  const voiceSeen = useRef(voiceLine);
  const voiceFresh = Boolean(voiceLine) && voiceLine !== voiceSeen.current;
  const shownTurn = showDrive
    ? driveTurn
    : typed.view
      ? { turn: typed.view.turn, at: typed.view.at, markdown: replyAfter(thread.messages, typed.view.at) }
      : lastTurn(thread.messages, { replying });
  // A live Talk session arrives open, and keeps the tray where it is as you move between pages;
  // a phone's tray always starts at its slim bar, the call's captions in it.
  const [detent, setDetent] = useState<TrayDetent>(talking && !mobile ? "medium" : "peek");
  const talkingRef = useRef(talking);
  talkingRef.current = talking && !mobile;
  const [docked, setDocked] = useLocalStorageState("kory.tray.docked", false);
  const column = useHostWidth(hostRef);
  const dockNow = trayDocks({ docked, column, needsWidth });
  // The screen can hold the side pane (a wide screen, with the shell's slot), whatever this page needs.
  const canSide = useSideTray();
  // The side pane covers nothing, so it stays as you left it.
  const side = canSide && dockNow;
  const open = detent !== "peek";
  // Report the computed presentation before paint. Overview derives its layout from this value,
  // so a floating → docked → floating round trip cannot retain the docked row.
  const trayMode: KoryTrayMode = mobile ? (open ? "phone-sheet" : "folded") : side && open ? "docked" : open ? "floating" : "folded";
  useLayoutEffect(() => {
    setTrayMode(trayMode);
  }, [setTrayMode, trayMode]);
  useEffect(() => () => setTrayMode("folded"), [setTrayMode]);
  // Product events (ids, counts and enums only): each detent change is an open, a close or a move.
  const scene: TrayScene = { viewport: mobile ? "phone" : "desktop", side_pane: side, session_on: sessionOn };
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const openSource = useRef<TrayOpenSource>("handle");
  const askSource = useRef<TrayOpenSource | null>(null);
  const navigated = useRef(false);
  const openedAt = useRef<Date | null>(null);
  const shownDetent = useRef(detent);
  useEffect(() => {
    const from = shownDetent.current;
    if (from === detent) return;
    shownDetent.current = detent;
    if (from === "peek") {
      openedAt.current = new Date();
      trayEvent("open", { source: openSource.current, detent }, sceneRef.current);
    } else if (detent === "peek") {
      trayClose(from, openedAt.current, navigated.current, sceneRef.current);
    } else {
      trayEvent("detent", { from, to: detent }, sceneRef.current);
    }
    openSource.current = "handle";
    navigated.current = false;
  }, [detent]);
  // A live Talk brings the tray back already open; an open tray that goes away (the flag turned off,
  // a page that hides it) still closes, as a navigation when the page changed under it.
  const shownPath = useRef(pathname);
  shownPath.current = pathname;
  useEffect(() => {
    // The parent's live route, read as it is when the tray goes away (not as it was here).
    const route = routePath;
    if (shownDetent.current !== "peek") {
      openedAt.current = new Date();
      trayEvent("open", { source: "talk", detent: shownDetent.current }, sceneRef.current);
    }
    return () => {
      const from = shownDetent.current;
      if (from === "peek") return;
      trayClose(from, openedAt.current, route.current !== shownPath.current, sceneRef.current);
    };
  }, [routePath]);
  // Working in the to-dos (scrolling them, opening or acting on a card) folds the Kory area to a line
  // and the composer row; tapping back up there, scrolling back to the top, or Kory speaking opens it.
  const headRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // The search card, on both layouts. The host brief reuses listRef for its own rows, so a lookup
  // there would miss the card that sits above them.
  const koryTalks =
    (talking && voice.turn.kory.tone === "speaking") || typed.view?.turn.kory.tone === "speaking" || Boolean(typed.view?.working);
  const reduce = usePrefersReducedMotion();
  const [zone, setZone] = useWorkingZone({ headRef, listRef, open, koryTalks, reduce });
  const compact = zone === "todos";
  // A host page's rows (Overview's), portaled in: the zone tracker reads them as the to-dos too.
  const briefRows = useCallback(
    (el: HTMLDivElement | null) => {
      listRef.current = el;
      brief?.(el);
    },
    [brief],
  );
  const namedReveal = useNamedReveal(open);
  const namedCard = useNamedCard();
  const call = useTrayCall(talking);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState("");
  // Unsent text holds the version reload (ReloadOnNewVersion).
  useReloadHold(draft.trim() !== "");
  const [failedSendSource, setFailedSendSource] = useState<TraySendSource | null>(null);
  // A turn you stop aborts its stream; that's not a failed send. Stop aborts every send still out,
  // and only those: a reply from another surface stops without touching a turn that already settled.
  const sendSeq = useRef(0);
  const pendingSends = useRef(new Set<number>());
  const stoppedSends = useRef(new Set<number>());
  // A phone shows the page chip once you tap into the composer or something is sent with the page.
  const [pageChipShown, setPageChipShown] = useState(false);
  const wantsFocus = useRef(false);

  // Never rises on navigation: a new page starts minimized, unless you are talking, a card is open,
  // or it was docked open beside the last page (on a page that needs the width it floats, still open).
  const cardOpenRef = useRef(drawer.isOpen);
  cardOpenRef.current = drawer.isOpen;
  const wasSideRef = useRef(side);
  useEffect(() => {
    if (talkingRef.current || wasSideRef.current || cardOpenRef.current) return;
    navigated.current = true;
    setDetent("peek");
  }, [pathname]);
  // Read by the effect above as it was before this render's page change.
  useEffect(() => {
    wasSideRef.current = side;
  });

  // Each ask (the header icon, K, a cell capture) raises it and focuses the composer.
  const seenOpen = useRef(openNonce);
  useEffect(() => {
    if (openNonce === seenOpen.current) return;
    seenOpen.current = openNonce;
    wantsFocus.current = true;
    // The header icon unless K or a capture said otherwise.
    const asked = askSource.current ?? "header";
    askSource.current = null;
    if (shownDetent.current === "peek") openSource.current = asked;
    setDetent((d) => (d === "peek" ? "medium" : d));
  }, [openNonce]);
  // "Work on this" raises a folded tray to the card it put in focus; the composer stays unfocused.
  const seenRaise = useRef(raiseNonce);
  useEffect(() => {
    if (raiseNonce === seenRaise.current) return;
    seenRaise.current = raiseNonce;
    setDetent((d) => (d === "peek" ? "medium" : d));
  }, [raiseNonce]);
  useEffect(() => {
    if (!open || !wantsFocus.current) return;
    wantsFocus.current = false;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, openNonce]);

  // A right-click capture asks the launcher to open; with the tray showing, it opens instead.
  useEffect(() => {
    if (launcherOpenNonce <= launcherHandledNonce.current) return;
    launcherHandledNonce.current = launcherOpenNonce;
    askSource.current = "capture";
    openTray();
  }, [launcherOpenNonce, launcherHandledNonce, openTray]);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isTrayShortcut(event)) return;
      event.preventDefault();
      askSource.current = "key";
      openTray();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openTray]);

  // Turns from other surfaces (the Overview card, a wake) show the next time it opens.
  const { reload } = thread;
  useEffect(() => {
    if (open && sessionOn) void reload();
  }, [open, sessionOn, reload]);
  // The focused Drive's own wakes, the same way.
  const driveConversation = drive ? (drivePage.data?.conversation_id ?? null) : null;
  const { refetch: reloadDrive } = driveTalk.history;
  useEffect(() => {
    if (open && driveConversation) void reloadDrive();
  }, [open, driveConversation, reloadDrive]);

  const traySubject: TraySubject | null = drive ? { type: "drive", id: drive.id } : null;
  // The page wins: what it lists itself stays out of the tray, so a to-do shows once on the screen.
  const excludes = useCallback((todo: TodoDTO) => trayExcludes(subject, todo), [subject]);
  const rows = useTrayRows(traySubject, excludes);
  // Overview and /todos list every to-do: the tray holds no list there (Overview keeps its picked card).
  const pageListsAll = !drive && pageShowsAllTodos(subject);
  const dealPage = !drive && typeof subject.shows === "object";
  // Focus mode: Overview's own gate (the New Overview switch or its browser override). On Overview the tray
  // reads the feed's own query, so its order and numbers are the page's.
  const focusMode = useRankedFeedGate();
  const preview = useKoryPreview();
  const previewing = preview !== null;
  // Preview shows the other person's day. The caller's own conversation with Kory stays out of it.
  const previewLine = preview ? previewingLine(preview.name) : null;
  const stopVoice = voice.stop;
  const voiceOn = voice.on;
  // A call already in progress ends when preview starts. Talk cannot start again until preview is off.
  useEffect(() => {
    if (previewing && voiceOn) stopVoice();
  }, [previewing, voiceOn, stopVoice]);
  const onOverview = subject.mode === "host";
  // Polled while focus mode is on, so a raise reaches the peek without a reload.
  // Preview uses the same feed, including on a Drive page, so the tray never falls
  // through to the caller's writable to-do list.
  const feed = useKoryFeed({ glance: onOverview, enabled: focusMode || previewing, poll: true });
  const raised = focusMode && feed.data?.focus?.door === "raised" ? feed.data.focus.reason : null;
  // What Kory points at, named on a phone's strip: the feed's items and the tray's to-dos.
  const trayContext = useKoryTray();
  const stripItems = useMemo(() => (feed.data?.items ?? []).map(toFeedItem), [feed.data]);
  // FocusTray holds the tray's card unless the page's brief rows or a Drive's own cards fill the tray.
  // Preview wins over that Drive branch, so Done and Snooze are not offered.
  // On /todos the page holds every card, so the tray holds none (Overview's tray still holds its picked one).
  const focusTrayOn = !(brief && !drive) && !(pageListsAll && !onOverview) && (previewing || (focusMode && !drive));
  // The tray card holds what FocusTray can, while it is there.
  const trayItems = useTrayItems(feed.data, onOverview, onOverview ? undefined : excludes);
  const trayHolds = useMemo(
    () => (focusTrayOn ? new Set(trayWork(trayItems).map((item) => item.key)) : new Set<string>()),
    [focusTrayOn, trayItems],
  );
  const openFromStrip = (key: string) => {
    const item = stripItems.find((candidate) => candidate.key === key);
    if (trayHolds.has(key)) trayContext?.pick(key);
    else if (item?.todo) drawer.open(item.todo.id, { seed: item.todo, follow: true });
    else document.querySelector(`[data-feed-item="${CSS.escape(key)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  };
  const stripTitles = useMemo(
    () => new Map([...rows.todos, ...rows.done].map((todo) => [todo.id, todo.title] as const)),
    [rows.todos, rows.done],
  );
  useLoadNamed([...rows.todos, ...rows.done], rows.ready);
  const selectedCell = attachedCells[0] ?? null;
  // Search as you type, through ⌘K's omnisearch; while you talk, the conversation is spoken.
  const search = useTraySearch(draft, traySearchLimit(), open && !talking);
  const [searchBandRows, clearSearchBandRows] = useSearchBandRows(search);
  // The last shown rows identify a click even after its navigation clears the draft.
  const stickySearchRef = useRef<TraySearch | null>(null);
  if (search) stickySearchRef.current = search;
  const openSearchResult = (url: string) => {
    const shown = stickySearchRef.current?.shown ?? [];
    const at = shown.findIndex((hit) => hit.url === url);
    const hit = at >= 0 ? shown[at] : undefined;
    trayEvent(
      "result_click",
      { kind: "search", position: at + 1 || null, todo_id: null, type: hit ? (hit.page ? "page" : hit.item?.type ?? null) : null },
      scene,
    );
    // A pick navigates away: the card leaves at once, never folding shut on a page you've left.
    stickySearchRef.current = null;
    clearSearchBandRows();
    setDraft("");
    navigate(url);
  };

  // A ⌘K handoff put back in the box keeps the palette's results for its retry.
  const heldResults = useRef<{ text: string; results: string | null } | null>(null);
  // A draft and a Kory turn typed here belong to one destination (a Drive, or Kory): when the tray's
  // destination changes they go, so words meant for one Drive never reach another, and an earlier
  // answer never covers the newly focused Drive's last turn.
  const destination = drive?.id ?? null;
  const draftFor = useRef(destination);
  const clearTyped = useRef(typed.clear);
  const voiceLineRef = useRef(voiceLine);
  voiceLineRef.current = voiceLine;
  clearTyped.current = typed.clear;
  useEffect(() => {
    if (draftFor.current === destination) return;
    draftFor.current = destination;
    setDraft("");
    clearTyped.current();
    voiceSeen.current = voiceLineRef.current;
  }, [destination]);
  // A ⌘K question put back in the box is still Kory's, edited or not, until the box is cleared or sent.
  const [koryRetry, setKoryRetry] = useState(false);
  useEffect(() => {
    if (!draft.trim()) setKoryRetry(false);
  }, [draft]);
  // With a Drive in focus, the box talks to it; a returned ⌘K question keeps it talking to Kory.
  const toDrive = Boolean(drive) && !koryRetry;
  // Queued ⌘K handoffs go one at a time; one that fails holds the rest until you send again.
  const [paletteGate, setPaletteGate] = useState<"open" | "sending" | "held">("open");
  const send = (text: string, source: TraySendSource = "composer", handed?: { results: string | null }) => {
    if (previewing) return;
    const value = text.trim();
    if (!value) return;
    // Picking a resting suggestion is a separate send. Keep the unfinished lookup in the box.
    const keepDraft = source === "ask_chip" && Boolean(draft.trim());
    // With a Drive in focus, your words go to it as a direction; a ⌘K handoff (and its retry) is still Kory's.
    const toKory = source === "palette" || koryRetry;
    if (drive && !toKory) {
      if (!driveWaking || driveTalk.sending || value.length > DRIVE_DIRECTIVE_MAX) {
        setDraft(value);
        return;
      }
      trayEvent("send", { source, chars: value.length, cells: 0, with_results: false }, scene);
      if (!keepDraft) setDraft("");
      setZone("kory");
      // The Drive's turn takes the place of any Kory turn a ⌘K question or a call left here.
      typed.clear();
      voiceSeen.current = voiceLine;
      driveTalk.send(value, () => setDraft((current) => (current ? `${value}\n\n${current}` : value)));
      return;
    }
    // Until the session read lands, a send can't know which conversation it belongs to; keep the words.
    if (session.isPending) {
      setDraft(value);
      return;
    }
    // The results you can see ride along, numbered as you see them, so "the second one" resolves;
    // a ⌘K handoff brings the ones the palette showed.
    // Only the retried question itself gets them, and only as the next send: any other words use
    // what's showing here, and drop them.
    const held = heldResults.current?.text === value ? heldResults.current : null;
    heldResults.current = null;
    const searchResults = (handed ? handed.results : held ? held.results : search?.block) ?? undefined;
    const about = askAbout ? askAboutContextLine(askAbout) : null;
    trayEvent(
      "send",
      { source, chars: value.length, cells: attachedCells.length, with_results: Boolean(searchResults) },
      scene,
    );
    if (sessionOn) {
      // One conversation everywhere: the page rides along model-only, and the reply shows here.
      const sentCells = attachedCells;
      const context = [
        searchResults ?? null,
        about,
        sentCells.length ? serializeCellContextBlock(sentCells) : null,
        capturePageContext(subjectContextLine(subject))?.text ?? null,
      ].filter(Boolean);
      if (!keepDraft) setDraft("");
      setFailedSendSource(null);
      setPageChipShown(true);
      setZone("kory");
      const sent = (sendSeq.current += 1);
      pendingSends.current.add(sent);
      // Your own send (a retry, or something else) lets the rest of the queue follow it.
      setPaletteGate(source === "palette" ? "sending" : "open");
      typed.start(value);
      // The values sent stay attached until the message lands (only those: a follow-up's stay put);
      // a failed send puts its words back ahead of anything typed since.
      const sentFrom = draftFor.current;
      void thread.send(value, { context: context.length ? context.join("\n\n") : undefined }).then((delivered) => {
        pendingSends.current.delete(sent);
        if (stoppedSends.current.delete(sent) || delivered) {
          for (const c of sentCells) removeCell(c.id);
          if (source === "palette") setPaletteGate((gate) => (gate === "sending" ? "open" : gate));
          return;
        }
        if (source === "palette") setPaletteGate("held");
        // Words go back only to the box they came from: if another destination took the tray meanwhile, they'd go there.
        if (draftFor.current !== sentFrom) return;
        // A ⌘K question (or its retry) failing again stays Kory's.
        if (toKory) setKoryRetry(true);
        if (!keepDraft) setDraft((current) => (current ? `${value}\n\n${current}` : value));
        setFailedSendSource(source);
      });
      return;
    }
    // Sent before navigating so the page context captured is this page, not /kory.
    void sendMessage(value, {
      startFresh: Boolean(selectedCell),
      hiddenContext: [subjectContextLine(subject), about].filter(Boolean).join("\n\n") || undefined,
      searchResults,
    });
    if (!keepDraft) setDraft("");
    setDetent("peek");
    navigate("/kory");
  };
  // A ⌘K handoff (⌘J, Tab twice, Enter on Ask) rises and sends its words here, as this conversation.
  const sendRef = useRef(send);
  sendRef.current = send;
  // It rises at once; the words wait for the session read, so they go to the right conversation.
  const seenPalette = useRef(paletteAsks.at(-1)?.nonce ?? 0);
  const raisedPalette = useRef(seenPalette.current);
  const sessionPending = session.isPending;
  // A failed read can't say which conversation: the words go back in the box, never to /kory.
  const sessionFailed = session.isError && !session.data;
  const threadSending = thread.sending;
  useEffect(() => {
    const fresh = paletteAsks.filter((ask) => ask.nonce > seenPalette.current);
    if (!fresh.length) return;
    const latest = fresh[fresh.length - 1].nonce;
    if (raisedPalette.current < latest) {
      raisedPalette.current = latest;
      askSource.current = "palette";
      // The page rides along with the ask, so a phone shows its chip as the tray rises.
      setPageChipShown(true);
      openTray();
    }
    // One turn at a time, in order: the next waits for the turn before it to finish.
    for (const ask of fresh) {
      // Preview does not send. The words stay in the box as Kory's, so Back to you
      // still asks Kory even when a Drive is focused.
      if (previewing) {
        seenPalette.current = ask.nonce;
        if (ask.text) {
          const text = ask.text;
          heldResults.current = { text: text.trim(), results: ask.results };
          setKoryRetry(true);
          setDraft((current) => (current ? `${text}\n\n${current}` : text));
        }
        continue;
      }
      if (ask.text && (sessionPending || (!sessionFailed && (threadSending || paletteGate !== "open")))) return;
      seenPalette.current = ask.nonce;
      if (!ask.text) continue;
      if (sessionFailed) {
        const text = ask.text;
        heldResults.current = { text: text.trim(), results: ask.results };
        setKoryRetry(true);
        setDraft((current) => (current ? `${text}\n\n${current}` : text));
        setFailedSendSource("palette");
        continue;
      }
      sendRef.current(ask.text, "palette", { results: ask.results });
      return;
    }
  }, [paletteAsks, openTray, sessionPending, sessionFailed, threadSending, paletteGate, previewing]);

  // While the lookup rows show, Arrow down or a plain Tab goes straight into them rather than the
  // Send button or a newline. A row still loading is disabled: leave the key alone so Tab can move
  // on, instead of trapping the composer.
  const intoSearchResults = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (search && !event.shiftKey && (event.key === "ArrowDown" || event.key === "Tab")) {
      const first = focusableSearchResult(headRef.current);
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send(draft);
      return;
    }
    intoSearchResults(event);
  };

  const driveTooLong = toDrive && draft.trim().length > DRIVE_DIRECTIVE_MAX;
  const driveBlocked = toDrive && (!driveWaking || driveTalk.sending || driveTooLong);
  const prompts = drive ? [] : selectedCell ? CELL_PROMPTS : subject.mode === "explain" ? EXPLAIN_PROMPTS : [];
  // The session's starters and next asks are Kory's; a Drive in focus has none of its own.
  // A numbers page or an attached cell offers its openers in the same band instead.
  // Preview sends nothing, and the session's asks are the caller's own.
  const asks: TrayAsks | null = previewing ? null : prompts.length ? { kind: "openers", asks: prompts } : drive ? null : trayAsks;
  const placeholder = previewing
    ? ""
    : toDrive
    ? drivePage.data && !driveWaking
      ? "Resume the Drive to message it."
      : "Tell this Drive…"
    : selectedCell
    ? "Or ask your own question…"
    : askAbout
      ? `Ask Kory about ${askAbout.label}…`
      : subject.label
      ? `Ask Kory about ${subject.label}…`
      : "Ask Kory…";

  const toggleTalk = () => {
    if (previewing) return;
    trayEvent("talk", { action: voice.on ? "stop" : "start" }, scene);
    if (voice.on) {
      voice.stop();
      return;
    }
    // The call's own turn replaces the typed one.
    typed.clear();
    void voice.start();
  };
  // While Kory works on a turn, Send's spot is Stop; the header carries Stop only while the composer is hidden.
  const stopTurn =
    previewing || !replying || !sessionOn || showDrive
      ? undefined
      : () => {
          if (pendingSends.current.has(sendSeq.current)) typed.stop();
          for (const sent of pendingSends.current) stoppedSends.current.add(sent);
          // ⌘K questions still waiting their turn are cancelled too.
          seenPalette.current = Math.max(seenPalette.current, paletteAsks.at(-1)?.nonce ?? 0);
          void thread.interrupt();
        };
  // A call or Meeting mode takes the talk unit's composer, so Stop moves to the header.
  const composerShown = open && !(talkOn && (voice.on || meeting.mode === "meeting"));
  const talkUnit = (
    <KoryTalkUnit
      voice={voice}
      mobile={mobile}
      onTalk={toggleTalk}
      rest={{ state: typed.view?.halo ?? live.state, level: null }}
      // The lookup rows show in the band under the card, so the last turn stays in place.
      turn={previewing ? null : showDrive ? (voiceFresh ? (composerTurn ?? driveTurn) : driveTurn) : composerTurn}
      compact={compact}
      composer={{
        ref: inputRef,
        value: draft,
        onChange: setDraft,
        onSend: () => send(draft),
        onFocus: () => {
          setComposerOpened(true);
          setPageChipShown(true);
        },
        onKeyDown: intoSearchResults,
        placeholder,
        label: toDrive ? "Message this Drive" : "Message Kory",
        disabled: previewing || (driveClosed && toDrive),
      }}
      send={{ disabled: previewing || !draft.trim() || driveBlocked, onStop: stopTurn }}
      liveLine={{ state: live.state, verb: live.verb }}
    />
  );

  // Folded, the line says a value rides along and opens to it; open, the header shows each value.
  const valueChip =
    selectedCell && !drive && !previewing && !open ? (
      <AttachedValueChip
        cells={attachedCells}
        onOpen={() => {
          openSource.current = "value_chip";
          setDetent("medium");
        }}
        onClear={clearCells}
      />
    ) : null;
  // A pinned Drive names itself on any page, as its own page does.
  const peekSubject = drive ? { ...subject, label: drive.label } : subject;
  const talkBar = koryTrayBar({
    voice,
    rest: { state: typed.view?.halo ?? live.state, level: null },
    idle: <TrayPeek subject={peekSubject} rows={rows} inBar raised={raised} />,
    onTalk: toggleTalk,
  });
  const bar = previewing
    ? {
        mark: talkBar.mark,
        status: <span className="truncate text-xs font-medium">{previewLine}</span>,
        actions: <PreviewBack tone="tray" />,
      }
    : valueChip
      ? { ...talkBar, actions: <>{valueChip}{talkBar.actions}</> }
      : talkBar;

  const header = open ? (
    <div ref={headRef} className="group/head kory-tray-prose space-y-2 font-sans text-sm leading-5" data-no-drag data-zone={zone}>
      {talkOn || previewing ? null : (drive || sessionOn) && shownTurn ? (
        <div data-testid="cos-last-turn">
          <LastTurnLines turn={shownTurn.turn} markdown={shownTurn.markdown} oneLine={compact} />
        </div>
      ) : null}
      {/* What rides along with a send: this page (the chip says how much) and any value you captured.
          A phone keeps its top for Kory: the row shows once you tap into the composer or send,
          or once something is attached. */}
      {!mobile || pageChipShown || askAbout || selectedCell || drive ? (
      <CaptionFold open={!compact}>
      {drive ? (
        <div className="flex items-center">
          <DriveFocusChip drive={drive} onPin={() => pinDrive(drive)} onUnpin={unpinDrive} />
        </div>
      ) : (
        // A direction carries only your words: with a Drive in focus, nothing else rides along.
        <TrayContextRow
          pathname={pathname}
          label={subject.label ?? (pageHeading() || null)}
          cells={attachedCells}
          onRemoveCell={removeCell}
          askAbout={askAbout}
          onAsk={() => {
            if (!askAbout) return;
            paletteEvent("chip_click", { kind: askAbout.type }, { viewport: scene.viewport });
            inputRef.current?.focus();
          }}
          onDismissAskAbout={() => {
            if (!askAbout) return;
            paletteEvent("chip_dismiss", { kind: askAbout.type }, { viewport: scene.viewport });
            setAskAbout(null);
          }}
        />
      )}
      </CaptionFold>
      ) : null}
      {previewing ? <PreviewTrayCue /> : null}
      {/* A phone's tray covers the page: what Kory points at is named here, above the box. */}
      {mobile ? (
        <PointingStrip
          items={stripItems}
          titles={stripTitles}
          focusKey={trayContext?.trayFocus?.key ?? null}
          onOpen={openFromStrip}
          onOpenTodo={(id) => drawer.open(id, { follow: true })}
        />
      ) : null}
      {talkOn ? (
        talkUnit
      ) : (
        <TrayComposer
          sessionOn={sessionOn}
          halo={typed.view?.halo ?? live.state}
          inputRef={inputRef}
          draft={draft}
          onDraft={setDraft}
          onFocus={() => setPageChipShown(true)}
          onKeyDown={onKeyDown}
          toDrive={toDrive}
          previewing={previewing}
          placeholder={placeholder}
          disabled={previewing || (driveClosed && toDrive)}
          sendDisabled={previewing || driveBlocked}
          stopTurn={stopTurn}
          onSend={() => send(draft)}
        />
      )}
      <SuggestionSearchBand
        talking={talking}
        search={search}
        rows={searchBandRows}
        asks={asks}
        compact={compact}
        mobile={mobile}
        onSend={send}
        onOpenResult={openSearchResult}
      />
      <TrayComposerStatus failedSendSource={failedSendSource} driveTooLong={driveTooLong} driveWaiting={Boolean(drive && driveTalk.waiting)} />
    </div>
  ) : null;

  return (
    // Menus, popovers and tooltips opened in the tray portal out of it; they take the tray's theme.
    <OverlayScope className="kory-tray-overlay">
    <DockedTraySideAllowed value={dockNow}>
    {/* With the keyboard up, a tap on any tray control (a pill, a lookup row, a header icon) lands first time. */}
    <div className="contents" onMouseDown={keepTrayTyping}>
    <DockedTray
      hostRef={hostRef}
      maxWidth={TRAY_WIDTH}
      align="end"
      sideWidth={TRAY_WIDTH}
      rightDrawer={{ widthRem: PANEL_REM, open: panels?.isOpen ?? drawer.isOpen }}
      label="Kory"
      data-testid="global-kory-tray"
      className="print:hidden"
      detent={detent}
      onDetent={setDetent}
      peek={
        previewing && detent === "peek" && !mobile && !canSide ? (
          <span className="truncate text-xs font-medium text-foreground">{previewLine}</span>
        ) : (
          <TrayPeek
            subject={peekSubject}
            rows={rows}
            mark={detent === "peek" || !talkOn}
            raised={raised}
            counts={mobile}
          />
        )
      }
      header={header}
      seeAll={
        drive || pathname === "/todos"
          ? undefined
          : {
              href: "/todos",
              // A phone's one header row keeps it short.
              label: `${mobile ? "All" : "See All"}${rows.ready && rows.todos.length ? ` ${rows.todos.length}` : ""} To-dos`,
              // Folded, it shares the handle row with the count, which keeps the room.
              short: "All To-dos",
            }
      }
      actions={
        <>
          {/* A phone's folded bar carries its own. */}
          {mobile ? null : valueChip}
          {previewing && detent === "peek" && !mobile && !canSide ? <PreviewBack tone="tray" /> : null}
          {showDrive && drive ? (
            // The Drive's whole conversation is its Thread tab, read-only; you talk to it here. While
            // a ⌘K question's Kory turn shows, Kory's own controls (and Stop) do.
            <ConversationActions conversationId={null} href={`/drives/${drive.id}?tab=thread`} />
          ) : sessionOn ? (
            <ConversationActions
              conversationId={sessionConversationId(session.data)}
              onStop={composerShown ? undefined : stopTurn}
            />
          ) : null}
          {previewing ? null : <KoryTraySettings voice={voice} />}
          {canSide ? <DockButton docked={docked} dockNow={dockNow} onDock={setDocked} /> : null}
        </>
      }
      reveal={namedReveal}
      named={namedCard}
      call={call}
      onFold={unmarkReferencedTodos}
      bar={bar}
      rail={{
        ...attachedValueRail(
          <KoryTrayHalo voice={voice} rest={{ state: typed.view?.halo ?? live.state, level: null }} size={32} />,
          attachedCells,
        ),
        actions: previewing ? (
          <PreviewBack tone="tray" compact />
        ) : talkOn ? (
          <KoryRailTalk
            voice={voice}
            onTalk={() => {
              if (!voice.on) {
                if (shownDetent.current === "peek") openSource.current = "talk";
                setDetent("medium");
              }
              toggleTalk();
            }}
          />
        ) : null,
      }}
    >
      <div className="kory-tray-prose font-sans text-sm leading-5">
      {brief && !drive ? (
        // The host page's own rows (Overview's brief and queue), portaled in by the page.
        <div ref={briefRows} data-tray-brief="" />
      ) : (
      <div ref={listRef} className="space-y-3">
        {drive ? <DriveHeadline driveId={drive.id} /> : null}
        {previewing ? null : <ThisMeetingSection />}
        {/* A Drive in focus holds its own cards; focus mode's feed is the tray's everywhere else. */}
        {focusTrayOn ? (
          <FocusTray
            feed={feed.data}
            ready={Boolean(feed.data)}
            failed={feed.isError && !feed.data}
            onOverview={onOverview}
            excludes={onOverview ? undefined : excludes}
            lens={!subject.shows}
            empty={dealPage ? "Nothing else needs you right now." : undefined}
          />
        ) : pageListsAll ? null : (
        <>
        {/* The page's own lens is about the page; a Drive in focus shows only that Drive's cards. A page
            that lists its own to-dos already shows what the lens would. */}
        {drive || previewing || subject.shows ? null : (
          <PageLens onOpen={(todo) => drawer.open(todo.id, { seed: todo, follow: true })} />
        )}
        {rows.failed ? (
          <p className="text-sm text-muted-foreground">Couldn&apos;t load your to-dos. Try again in a moment.</p>
        ) : !rows.ready ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full rounded-md" />
            <Skeleton className="h-12 w-full rounded-md" />
          </div>
        ) : (
          // Mounted even when nothing is open, so a last card finishing still shows its confirmation.
          <TodoTray
            todos={[...rows.todos, ...rows.done]}
            ready={rows.ready}
            empty={
              <p className="text-sm text-muted-foreground">
                {drive
                  ? "Nothing from this Drive needs you right now."
                  : dealPage
                    ? "Nothing else needs you right now."
                    : "Nothing needs you right now."}
              </p>
            }
            activeId={drawer.activeKey ?? undefined}
            subject={traySubject ?? undefined}
            limit={mobile ? PHONE_TRAY_TOP : TRAY_TOP}
            onOpen={(todo) => {
              // The tray groups and trims its rows, so the queue index isn't where it showed.
              trayEvent("result_click", { kind: "todo", position: null, todo_id: todo.id, type: null }, scene);
              drawer.open(todo.id, { seed: todo, follow: true });
            }}
          />
        )}
        </>
        )}
      </div>
      )}
      </div>
    </DockedTray>
    </div>
    </DockedTraySideAllowed>
    </OverlayScope>
  );
}

/** Dock the tray beside pages with room for it, or float it over every page. */
function DockButton({ docked, dockNow, onDock }: { docked: boolean; dockNow: boolean; onDock: (docked: boolean) => void }) {
  // Docked, but this page needs the width: the tray floats here and docks again on the next page with room.
  const label = !docked ? "Dock Beside Page" : dockNow ? "Float Over Page" : "Float (This Page Needs the Width)";
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 shrink-0 text-muted-foreground"
      aria-label={label}
      aria-pressed={docked}
      title={label}
      onClick={() => onDock(!docked)}
    >
      {docked ? <PanelRightDashed className="size-4" aria-hidden /> : <PanelRight className="size-4" aria-hidden />}
    </Button>
  );
}

/** The quiet count beside the Kory mark; null while the rows load. */
function peekCount(subject: KoryPageSubject, rows: TrayRows): string | null {
  // The page lists every to-do itself; the tray counts none of them.
  if (pageShowsAllTodos(subject)) return null;
  if (rows.failed) return "couldn't load to-dos";
  if (!rows.ready) return null;
  const needYou = rows.todos.filter((t) => trayBucket(t) === "needs_you").length;
  const fresh = rows.todos.filter((t) => trayBucket(t) === "since_you_left").length;
  const work = needYou ? `${needYou} need${needYou === 1 ? "s" : ""} your yes` : "all clear";
  return fresh ? `${work} · ${fresh} new` : work;
}

function TrayPeek({
  subject,
  rows,
  mark = false,
  inBar = false,
  raised = null,
  counts = true,
}: {
  subject: KoryPageSubject;
  rows: TrayRows;
  /** The Kory mark, the line's identity wherever the Talk leaf isn't right under it (folded, or no Talk). */
  mark?: boolean;
  /** A phone's bar, beside its halo instead of the mark. */
  inBar?: boolean;
  /** Door 2: why Kory raised something into the tray. The peek turns amber; the tray never opens for it. */
  raised?: string | null;
  /** The to-do counts beside Kory. A desktop tray leaves them to its rows; a failed load still says so. */
  counts?: boolean;
}) {
  const count = counts || rows.failed ? peekCount(subject, rows) : null;
  const theme = useTrayTheme();
  return (
    <span
      className={cn("flex min-w-0 items-center gap-2", raised && "text-warning-ink")}
      data-testid={inBar ? undefined : "global-kory-tray-peek"}
      data-raised={raised ? true : undefined}
    >
      {mark && !inBar ? <KoryIcon variant={trayIconVariant(theme)} size={18} alt="" /> : null}
      {raised ? <span aria-hidden className="size-2 shrink-0 rounded-full bg-warning" /> : null}
      <span className="flex min-w-0 items-center gap-1">
        <span className="shrink-0 font-semibold">Kory</span>
        {raised ? (
          <span className="truncate"> · {raised}</span>
        ) : (
          <>
            {/* With the counts, the page label gives way first; the counts still truncate rather than run under the bar's controls. */}
            {subject.label ? (
              <span className={cn("min-w-0 truncate text-muted-foreground", counts && "shrink-[4]")}> · {subject.label} </span>
            ) : null}
            {count ? (
              <span data-testid="kory-tray-count" className="min-w-0 truncate whitespace-nowrap text-muted-foreground">
                · {count}
              </span>
            ) : null}
          </>
        )}
      </span>
    </span>
  );
}
